import { Events, ScriptingObjectTypes } from '@elliemae/pui-scripting-object';
import { Logger } from '@elliemae/pui-diagnostics';
import type {
  EventListeners,
  GuestContext,
  CallerInfo,
  SubscribeParam,
  UnsubscribeParam,
} from '@elliemae/microfe-common';
import {
  Event,
  ScriptingObjectManager,
  ScriptingObjects,
  isPublicFunction,
  ScriptingObjectProxy,
} from '@elliemae/microfe-common';
import { EventManager } from './eventManager.js';
import { IMicroFEHost, AppWindowSize } from './typings/host.js';
import { LATEST_VERSION } from './constant.js';

/**
 * Constructor parameters for the host application
 */
export type CMicroFEHostParams<
  AppObjects extends ScriptingObjects,
  AppEvents extends EventListeners,
> = {
  /**
   * unique id of the iframe container
   */
  containerId?: string;
  /**
   * unique id of the guest application
   */
  guest: GuestContext;
  /**
   * release version of the host application
   */
  version?: string;
  /**
   * instance of pui diagnostics logger
   */
  logger: Logger;
  /**
   * instance of scripting object manager
   */
  soManager: ScriptingObjectManager<AppObjects>;
  /**
   * instance of event manager
   */
  eventManager: EventManager<AppEvents>;
};

/**
 * Represents a host micro frontend application
 * @typeParam AppObjects - scripting objects that are published by the host application
 * @typeParam AppEvents - events that are published by the host application
 */
export class CMicroFEHost<
  AppObjects extends ScriptingObjects = Partial<ScriptingObjectTypes>,
  AppEvents extends EventListeners = Events,
> implements IMicroFEHost<AppObjects, AppEvents>
{
  /**
   * unique id of the iframe container
   */
  #containerId?: string;

  #guest: GuestContext;

  #version: string;

  #soManager: ScriptingObjectManager<AppObjects>;

  #eventManager: EventManager<AppEvents>;

  /**
   * Create new instance of the host frontend application
   * @param {CMicroFEHostParams} params - constructor parameters
   */
  constructor(params: CMicroFEHostParams<AppObjects, AppEvents>) {
    this.#guest = params.guest;
    this.#containerId = params.containerId;
    this.#version = params?.version || LATEST_VERSION;
    this.#soManager = params.soManager;
    this.#eventManager = params.eventManager;
  }

  /**
   * application release version
   * @returns release version
   */
  get version() {
    return this.#version;
  }

  /**
   * Get reference to the scripting object (or proxy) by name
   * @param name unique name of the scripting object
   * @param objectId
   * @returns scripting object reference
   */
  getObject = <ObjectId extends Extract<keyof AppObjects, string>>(
    objectId: ObjectId,
  ): Promise<AppObjects[ObjectId] | null> => {
    try {
      const so = this.#soManager.getObject(objectId, this.#guest);
      if (!so) return Promise.resolve(null);
      // create a proxy object for the scripting object
      const proxy = new ScriptingObjectProxy(so.id, so.objectType);
      Object.keys(so).forEach((propName) => {
        const propValue = so[propName as keyof AppObjects[ObjectId]];
        if (propValue instanceof Event) {
          Object.defineProperty(proxy, propName, {
            value: new Event<AppEvents>({
              name: propName,
              objectId: so.id,
            }),
            enumerable: true,
          });
        } else if (isPublicFunction(propValue, propName)) {
          Object.defineProperty(proxy, propName, {
            value: (...args: any[]) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              const existingCallChain = (proxy as any)[propName]?.callContext
                ?.callChain as CallerInfo[] | undefined;
              Object.defineProperty(propValue, 'callContext', {
                value: {
                  guest: this.#guest,
                  ...(existingCallChain?.length
                    ? { callChain: existingCallChain }
                    : {}),
                },
                configurable: true,
                enumerable: true,
                writable: true,
              });
              // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
              return (so[propName as keyof AppObjects[ObjectId]] as Function)(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                ...args,
              );
            },
            enumerable: true,
          });
        }
      });
      return Promise.resolve(proxy as unknown as AppObjects[ObjectId]);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /**
   * set the size of the guest application iframe window
   * @param {AppWindowSize} appSize window size of the application
   */
  setAppWindowSize = (appSize: AppWindowSize) => {
    if (!this.#containerId) return;
    const { size } = appSize;
    const frameEle = document.getElementById(this.#containerId);
    if (frameEle) {
      frameEle.style.height = `${size.height}px`;
      frameEle.style.width = `${size.width}px`;
    }
  };

  /**
   * add listener to the scripting object event
   * @param {SubscribeParam<EventId, EventListener>} params - parameters to add event listener
   * @returns subscription id
   */
  subscribe = <EventId extends Extract<keyof AppEvents, string>>(
    params: SubscribeParam<EventId, AppEvents[EventId]>,
  ) => this.#eventManager.subscribe(params);

  /**
   * removes listener from the scripting object event
   * @param {UnsubscribeParam<EventId>} params - parameters to remove event listener
   */
  unsubscribe = <EventId extends Extract<keyof AppEvents, string>>(
    params: UnsubscribeParam<EventId>,
  ) => {
    this.#eventManager.unsubscribe(params);
  };
}
